// Phase 24 M4 — React AuthContext.
//
// Wraps the M1 `authStore` (localStorage backed) with a Context provider
// + the password / Google / register actions M4 needs. Components
// consume the hook via `useAuthContext()`.
//
// Tokens live in localStorage (same as M1; see the rationale in
// auth/store.ts). The iter-3 review flagged refresh tokens in
// localStorage as XSS-exfiltratable — we acknowledge the trade-off:
// the alternative (httpOnly cookie) would force a second auth path
// for the same monorepo (mobile + web), break the `Authorization:
// Bearer` semantics every other route expects, and trip CORS on the
// api.chain.leyro.es origin separation. Mitigations stay layered
// (strict CSP, short access TTL, server-side refresh rotation +
// revocation), which is the standard compromise SPA+API stacks
// make.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { AuthenticatedUser, TokenPair } from '@leyrochain/shared';

import {
  loginWithGoogle as apiLoginWithGoogle,
  loginWithPassword as apiLoginWithPassword,
  logoutSession,
  refreshSession,
  registerUser,
} from '../api/auth';
import { authStore } from '../auth/store';

export interface AuthContextValue {
  user: AuthenticatedUser | null;
  accessToken: string | null;
  loading: boolean;
  needsOnboarding: boolean;
  login: (email: string, password: string) => Promise<TokenPair>;
  loginWithGoogle: (idToken: string, language?: 'gl' | 'es') => Promise<TokenPair>;
  register: (input: {
    email: string;
    name: string;
    password: string;
    language?: 'gl' | 'es';
    turnstileToken?: string;
    // Phase 25 iter-5a — roles seleccionados no AuthModal. O primeiro
    // marca o primary_role; os restantes engádense vía ensureRole no
    // backend. Opcional para compatibilidade con flows que aínda non
    // pidan o paso (Google Sign-In etc.).
    roles?: Array<'FARMER' | 'BUYER' | 'VET' | 'TRANSPORTISTA'>;
  }) => Promise<TokenPair>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setSession: (tokens: TokenPair) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [snapshot, setSnapshot] = useState(authStore.getSnapshot());
  const [loading, setLoading] = useState(false);

  useEffect(() => authStore.subscribe(setSnapshot), []);

  const setSession = useCallback((tokens: TokenPair) => {
    authStore.setSession(tokens);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const tokens = await apiLoginWithPassword({ email, password });
      authStore.setSession(tokens);
      return tokens;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string, language?: 'gl' | 'es') => {
    setLoading(true);
    try {
      const tokens = await apiLoginWithGoogle({ idToken, language });
      authStore.setSession(tokens);
      return tokens;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback<AuthContextValue['register']>(async (input) => {
    setLoading(true);
    try {
      const tokens = await registerUser(input);
      authStore.setSession(tokens);
      return tokens;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    const refresh = authStore.getSnapshot().refreshToken;
    if (refresh) {
      try {
        await logoutSession(refresh);
      } catch {
        // Best-effort revocation; logout always clears the local session.
      }
    }
    authStore.logout();
  }, []);

  const refreshUser = useCallback(async () => {
    const refresh = authStore.getSnapshot().refreshToken;
    if (!refresh) return;
    try {
      const tokens = await refreshSession(refresh);
      authStore.setSession(tokens);
    } catch {
      authStore.logout();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: snapshot.user,
      accessToken: snapshot.accessToken,
      needsOnboarding: snapshot.needsOnboarding,
      loading,
      login,
      loginWithGoogle,
      register,
      logout,
      refreshUser,
      setSession,
    }),
    [snapshot, loading, login, loginWithGoogle, register, logout, refreshUser, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used inside <AuthProvider>');
  }
  return ctx;
}
